import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { useContext } from 'react'
import styled from 'styled-components'
import { getThemeClassName } from 'lib/theme/themeUtils'
import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'
import SolidPlayIcon from 'LuxKit/Icons/solid/SolidPlayIcon'
import HeightFillingImage from 'components/Common/HeightFillingImage'
import VideoLightbox from 'LuxKit/Carousel/VideoLightbox'
import ModalContext from 'contexts/ModalContext'
import { renderMarkdown } from 'lib/string/markdownUtils'
import clsx from 'clsx'
import Caption from 'LuxKit/Typography/Caption'
import TextButton from 'LuxKit/Button/TextButton'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import Sheet from 'components/utils/Sheet'

const Root = styled(Sheet)`
  &.background-color-white {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  }

  &.background-color-grey {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  }
`

const Grid = styled(Pane)`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  &.background-color-white {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  }

  &.background-color-grey {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  }

  &.show-image {
    grid-template-areas:
    'content'
    'video';

    &.content-position-end {
      grid-template-areas:
        'video'
        'content';

      ${mediaQueryUp.tablet} {
        grid-template-areas: 'video content';
      }
    }

    ${mediaQueryUp.tablet} {
      grid-template-columns: 1fr 1fr;
      grid-template-areas: 'content video';
    }
  }

  width: 100%;
  display: grid;
  gap: ${rem(20)};
  padding: ${rem(32)} 0;

  ${mediaQueryUp.tablet} {
    gap: ${rem(20)} ${rem(40)};
    padding: ${rem(60)} 0;
    align-items: center;
  }
`

const Content = styled(Group)`
  grid-area: content;
`

const Video = styled.div`
  grid-area: video;
`

const PlayButton = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transition: transform 0.2s;
  transform: translateY(-50%) translateX(-50%) scale(1);
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${rem(48)};
  width: ${rem(48)};

  ${mediaQueryDown.mobile} {
    height: ${rem(36)};
    width: ${rem(36)};
  }
`

const StyledPlayIcon = styled(SolidPlayIcon)`
  height: ${rem(24)};
  width: ${rem(24)};

  ${mediaQueryDown.mobile} {
    height: ${rem(20)};
    width: ${rem(20)};
  }
`

const VimeoWrapper = styled.div`
  width: 100%;
  position: relative;
  background-color: ${props => props.theme.palette.neutral.surface.darkBackground};

  > * {
    pointer-events: none;
  }

  &:focus, &:hover {
    > ${PlayButton} {
      transform: translateY(-50%) translateX(-50%) scale(1.2);
    }
  }
`

export function ContentfulVideoBlock({ title, subtitle, bodyText, videoId, image, backgroundColor, caption, contentPosition, buttonBlocks, contentAlign, buttonDirection }: App.ComponentVideoBlock) {
  const markedBodyText = renderMarkdown(bodyText)
  const showLightbox = useContext(ModalContext)
  return (
    <Root paletteType={backgroundColor === 'dark' ? 'inverse' : 'default'} background="normal-background" className={`background-color-${backgroundColor}`}>
      <LayoutContainer>
        <Grid className={clsx(`background-color-${backgroundColor}`, `content-position-${contentPosition}`, { 'show-image': image.id })}>
          <Content direction="vertical" gap={16} desktopGap={20}>
            <Group direction="vertical" gap={4}>
              {!!caption && <Caption variant="small" colour="light-foreground-alt" weight="bold" align={contentAlign}>{caption}</Caption>}
              <Heading variant="heading2" colour="normal-contrast" dangerouslySetInnerHTML={{ __html: title }} align={contentAlign} />
              {!!subtitle && <BodyText variant="large" colour="light-foreground-alt" align={contentAlign}>{subtitle}</BodyText>}
            </Group>
            <BodyText variant="large" colour="light-foreground-alt" dangerouslySetInnerHTML={{ __html: markedBodyText }} align={contentAlign} />
            {!!buttonBlocks?.length && <Group direction={buttonDirection} gap={8} horizontalAlign={contentAlign}>
              {buttonBlocks.map((buttonBlock, index) =>
                <ProductPaletteProvider palette={buttonBlock.productTheme} key={`${index}-${buttonBlock.title}`}>
                  <TextButton
                    fit={buttonBlock.fit}
                    kind={buttonBlock.kind}
                    {...(buttonBlock.linkTo.startsWith('http') ? { href: buttonBlock.linkTo } : { to: buttonBlock.linkTo })}
                    size={buttonBlock.size}
                  >
                    {buttonBlock.title}
                  </TextButton>
                </ProductPaletteProvider>)}
            </Group>}
          </Content>
          {!!image.id && <Video>
            {!!videoId && <VimeoWrapper className={getThemeClassName({ mobile: 'default' })} onClick={() => showLightbox(<VideoLightbox videoId={videoId} playOnMount />)}>
              <HeightFillingImage
                fit="center"
                id={image.id}
                alt={title}
                mobileAspectRatio="16:9"
              />
              <PlayButton>
                <StyledPlayIcon />
              </PlayButton>
            </VimeoWrapper>}
            {!videoId && <HeightFillingImage
              fit="center"
              id={image.id}
              alt={title}
              mobileAspectRatio="16:9"
            />}
          </Video>}
        </Grid>
      </LayoutContainer>
    </Root>
  )
}
